import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DialogControl, FieldControl } from "spark-core-dx/components";

const firstItem = (items) => {
  if (!items) return null;
  if (typeof items.first === "function") return items.first();
  return Array.isArray(items) ? items[0] : null;
};

const getValue = (item, ...keys) => {
  if (!item) return "";
  for (const key of keys) {
    const value = item[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return "";
};

const getString = (item, ...keys) => String(getValue(item, ...keys) ?? "").trim();

const getId = (item) => getString(item, "_id", "Id", "id", "UniqueId", "uniqueId");

const getArray = (item, ...keys) => {
  if (!item) return [];
  for (const key of keys) {
    const value = item[key];
    if (Array.isArray(value)) return value;
  }
  return [];
};

const getOperationResult = (response) => {
  const firstResponse = firstItem(response);
  return firstResponse?.Items || firstResponse?.Success !== undefined || firstResponse?.success !== undefined
    ? firstResponse
    : response;
};

const getManagerItemFromResponse = (response) => {
  const result = getOperationResult(response);
  return firstItem(getArray(result, "Items", "items"))
    || firstItem(getArray(response, "Items", "items"))
    || (result && typeof result === "object" && !Array.isArray(result) && result.Success === undefined && result.success === undefined ? result : null)
    || (response && typeof response === "object" && !Array.isArray(response) && response.Success === undefined && response.success === undefined ? response : null);
};

const getLowerCamelKey = (fieldName) =>
  fieldName ? fieldName.charAt(0).toLowerCase() + fieldName.slice(1) : "";

const isNotConfiguredValue = (value) =>
  ["notconfigured", "not configured"].includes(String(value ?? "").trim().toLowerCase());

const isNotConfiguredOption = (option) =>
  isNotConfiguredValue(option?.Value ?? option?.value ?? option?.Text ?? option?.text ?? option?.Label ?? option?.label ?? option);

const filterNotConfiguredOptions = (fieldMetadata) => {
  if (!fieldMetadata) return fieldMetadata;

  const filteredMetadata = { ...fieldMetadata };
  ["DataJson", "dataJson", "Options", "options", "SelectOptions", "selectOptions"].forEach((key) => {
    if (Array.isArray(filteredMetadata[key])) {
      filteredMetadata[key] = filteredMetadata[key].filter((option) => !isNotConfiguredOption(option));
    }
  });

  return filteredMetadata;
};

const PolicyFieldRepairDialog = ({
  open,
  item,
  itemManager,
  fieldMetadata,
  fieldName,
  title,
  description,
  saveButtonText = "Save",
  onSave,
  onClose
}) => {
  const [value, setValue] = useState("");
  const [state, setState] = useState({ busy: false, error: "" });
  const effectiveFieldName = fieldName || getString(fieldMetadata, "FieldName", "fieldName", "Name", "name");
  const repairFieldMetadata = useMemo(() => filterNotConfiguredOptions(fieldMetadata), [fieldMetadata]);

  useEffect(() => {
    if (!open) return;
    const currentValue = getValue(item, effectiveFieldName, getLowerCamelKey(effectiveFieldName));
    setValue(isNotConfiguredValue(currentValue) ? "" : currentValue);
    setState({ busy: false, error: "" });
  }, [effectiveFieldName, item, open]);

  const saveRepair = useCallback(async () => {
    if (!effectiveFieldName || !repairFieldMetadata) {
      setState({ busy: false, error: "Policy repair field metadata is not available." });
      return;
    }
    if (!itemManager?.getItem || !onSave) {
      setState({ busy: false, error: "Policy repair service is not available." });
      return;
    }

    const itemId = getId(item);
    if (!itemId) {
      setState({ busy: false, error: "Policy repair item is missing an id." });
      return;
    }

    setState({ busy: true, error: "" });
    try {
      const latestResponse = await itemManager.getItem(itemId);
      const latestItem = getManagerItemFromResponse(latestResponse);
      if (!latestItem) {
        throw new Error("Latest policy could not be loaded.");
      }

      const saveResult = await onSave({
        ...latestItem,
        [effectiveFieldName]: value
      });
      if (saveResult) {
        setState({ busy: false, error: "" });
        onClose?.();
      } else {
        setState({ busy: false, error: "Policy repair could not be saved." });
      }
    } catch (repairError) {
      setState({ busy: false, error: repairError?.message || "Policy repair could not be saved." });
    }
  }, [effectiveFieldName, repairFieldMetadata, item, itemManager, onClose, onSave, value]);

  return (
    <DialogControl
      controlOrigin="policy-field-repair"
      title={title || "Edit Policy"}
      openDialog={open}
      okText={state.busy ? "Saving..." : saveButtonText}
      cancelText="Cancel"
      disableOk={state.busy || !repairFieldMetadata}
      loadingOk={state.busy}
      onOk={saveRepair}
      onCancel={onClose}
      onX={onClose}
    >
      <div className="policy-field-repair-dialog">
        {description ? <p>{description}</p> : null}
        {state.error ? <div className="policy-field-repair-dialog__error" role="alert">{state.error}</div> : null}
        {repairFieldMetadata ? (
          <FieldControl
            value={value}
            onChange={setValue}
            fieldMetadata={repairFieldMetadata}
          />
        ) : (
          <div className="policy-field-repair-dialog__error" role="alert">Policy repair field metadata is not available.</div>
        )}
      </div>
    </DialogControl>
  );
};

export default PolicyFieldRepairDialog;
